import { z } from 'zod/v3';

import {
  frontDiscountItemSchema,
  paymentResultSchema,
  productOrderClaimStatusSchema,
  productOrderSchema,
} from './order';

// ============================================================================
// 교환/반품(클레임) 사유·해결방안 Enum
// ============================================================================

/** 클레임 문제 타입 */
export const claimReasonTypeSchema = z.enum([
  'NONE',
  'NOT_ARRIVAL_AND_DELAY_IN_DELIVERY',
  'FAULTY_PRODUCT',
  'WRONG_DELIVERY',
  'CHANGE_OF_MIND',
]);
export type ClaimReasonType = z.infer<typeof claimReasonTypeSchema>;

/** 클레임 해결 방안 타입 */
export const claimSolutionTypeSchema = z.enum([
  'NONE',
  'PARTIAL_REFUND',
  'CANCELLATION_WITH_REFUND',
  'RETURNED_WITH_REFUND',
  'RETURNED_WITH_REFUND_EXCEPT_DELIVERY_FEE',
  'EXCHANGE_REDELIVERY',
  'EXCHANGE_REDELIVERY_EXCEPT_DELIVERY_FEE',
  'NEXT_DAY_DELIVERY',
]);
export type ClaimSolutionType = z.infer<typeof claimSolutionTypeSchema>;

/** 클레임 상세 상태 */
export const claimDetailStatusSchema = z.enum([
  'NONE',
  'REQUEST',
  'PROGRESS',
  'COMPLETED',
  'RESTORED',
]);
export type ClaimDetailStatus = z.infer<typeof claimDetailStatusSchema>;

// ============================================================================
// Enum 스키마
// ============================================================================

/** 취소 사유 */
export const cancelReasonSchema = z.enum([
  'NONE',
  'CHANGE_OF_MIND',
  'WRONG_ORDER',
  'DELAY_IN_DELIVERY',
  'FAULTY_PRODUCT',
  'WRONG_PRODUCT',
  'NON_ARRIVAL',
  'CANCEL_ADMIN',
]);
export type CancelReason = z.infer<typeof cancelReasonSchema>;

/** 전체 취소 사유 */
export const allCancelTypeSchema = z.enum([
  'BUNDLE_AND_CONDITIONALLY_FREE',
  'COUPON',
]);
export type AllCancelType = z.infer<typeof allCancelTypeSchema>;

/** 클레임 환불 상태 */
export const claimRefundStatusSchema = z.enum([
  'NONE',
  'REQUESTED',
  'IN_PROGRESS',
  'REFUNDED',
  'FAILED',
  'FORCE_COMPLETED',
]);
export type ClaimRefundStatus = z.infer<typeof claimRefundStatusSchema>;

/** 주문 취소 포인트 처리 유형 */
export const orderCancelPointTypeSchema = z.enum([
  'NONE',
  'EXPECTED_CANCEL',
  'POINT_RECOVERY',
  'REFUND_DEDUCTION',
]);
export type OrderCancelPointType = z.infer<typeof orderCancelPointTypeSchema>;

// ============================================================================
// 취소 요청 스키마 (MarketCancelProductOrdersRequest)
// ============================================================================

/**
 * 상품 주문 취소 요청 스키마
 */
export const cancelProductOrdersRequestSchema = z.object({
  /** 취소 상세 사유 */
  detailReason: z.string().optional(),
  /** 취소할 상품 주문 ID 리스트 */
  productOrderIds: z.array(z.number()),
  /** 취소 사유 */
  reason: cancelReasonSchema,
});
export type CancelProductOrdersRequest = z.infer<
  typeof cancelProductOrdersRequestSchema
>;

// ============================================================================
// 취소 가능 상품 응답 스키마 (MarketCancellableProductOrderResponse)
// ============================================================================

/**
 * 취소 가능한 상품 주문 응답 스키마
 */
export const cancellableProductOrderSchema = z.object({
  /** 전체 취소 여부 (legacy) */
  allCancel: z.boolean().optional(),
  /** 전체 취소 사유 */
  allCancelType: allCancelTypeSchema.optional(),
  /** 전체 취소 여부 */
  isAllCancel: z.boolean(),
  /** 결제 결과 정보 */
  paymentResult: paymentResultSchema.optional(),
  /** 상품 주문 리스트 */
  productOrders: z.array(productOrderSchema).optional(),
});
export type CancellableProductOrder = z.infer<
  typeof cancellableProductOrderSchema
>;

// ============================================================================
// 클레임 상세 스키마 (MarketOrderClaimResponse)
// ============================================================================

/**
 * 주문 취소 시 포인트 처리 정보 스키마 (MarketOrderCancelPointResponse)
 */
export const orderCancelPointSchema = z.object({
  /** 노출 금액 (포인트) */
  amount: z.number(),
  /** 포인트 처리 유형 */
  type: orderCancelPointTypeSchema,
});
export type OrderCancelPoint = z.infer<typeof orderCancelPointSchema>;

/**
 * 클레임 환불 응답 스키마 (MarketOrderClaimRefundResponse)
 */
export const orderClaimRefundSchema = z.object({
  /** 환불 ID */
  id: z.number().optional(),
  /** 종결일 (ISO-8601, KST) */
  processedAt: z.string().optional(),
  /** 환불 추가 배송비 (+/-) */
  refundAdditionalDeliveryFee: z.number().optional(),
  /** 환불 배송비 (+/-) */
  refundDeliveryFee: z.number().optional(),
  /** 환불 할인 금액 */
  refundDiscountAmount: z.number().optional(),
  /** 환불 할인 항목 */
  refundDiscountItems: z.array(frontDiscountItemSchema),
  /** 환불 상품 금액 (할인 전) */
  refundOriginProductPrice: z.number().optional(),
  /** 환불 플러스 멤버십 할인 금액 */
  refundPlusMembershipDiscountAmount: z.number().optional(),
  /** 환불 상품 금액 (할인 후) */
  refundProductPrice: z.number().optional(),
  /** 총 환불비 */
  refundTotalAmount: z.number().optional(),
  /** 총 환불 포인트 금액 */
  refundTotalPointAmount: z.number().optional(),
  /** 환불 상태 */
  status: claimRefundStatusSchema.optional(),
});
export type OrderClaimRefund = z.infer<typeof orderClaimRefundSchema>;

/**
 * 클레임 응답 스키마 (MarketOrderClaimResponse)
 */
export const orderClaimSchema = z.object({
  /** 포인트 처리 정보 */
  cancelPoint: orderCancelPointSchema,
  /** 클레임 번호 */
  code: z.string().optional(),
  /** 클레임 생성일 (ISO-8601, KST) */
  createdAt: z.string().optional(),
  /** 상세사유 */
  detailReason: z.string().optional(),
  /** 반품/교환 수수료 */
  fee: z.number().optional(),
  /** 클레임 ID */
  id: z.number().optional(),
  /** PG 환불액 조정 여부 */
  pgAdjusted: z.boolean(),
  /** 반품/교환 수거 방식 */
  pickUpMethod: z.string().optional(),
  /** 종결일 (ISO-8601, KST) */
  processedAt: z.string().optional(),
  /** 클레임 주문 정보 */
  productOrder: productOrderSchema.optional(),
  /** 접수사유 (귀책사유) */
  reason: z.string().optional(),
  /** 회수된 포인트 금액 */
  recoveredPoint: z.number(),
  /** 클레임 환불 응답 정보 */
  refund: orderClaimRefundSchema.optional(),
  /** 철회사유 */
  restorationReason: z.string().optional(),
  /** 클레임 상태 */
  status: productOrderClaimStatusSchema.optional(),
});
export type OrderClaim = z.infer<typeof orderClaimSchema>;

// ============================================================================
// 교환/반품 사유 목록 (ClaimReasonResponse)
// ============================================================================

/**
 * 클레임 해결 방안 스키마 (ClaimSolutionResponse)
 */
export const claimSolutionSchema = z.object({
  /** 해결 방안 설명 */
  description: z.string().optional(),
  /** 해결 방안 ID */
  id: z.number().optional(),
  /** 해결 방안 타입 */
  type: claimSolutionTypeSchema.optional(),
});
export type ClaimSolution = z.infer<typeof claimSolutionSchema>;

/**
 * 클레임 문제(사유) 스키마 (ClaimReasonResponse)
 */
export const claimReasonItemSchema = z.object({
  /** 문제 설명 */
  description: z.string().optional(),
  /** 문제 ID */
  id: z.number().optional(),
  /** 고객 안내 메시지 */
  message: z.string().optional(),
  /** 이미지 필요 여부 */
  needImage: z.boolean().optional(),
  /** 해결 방안 목록 */
  solutions: z.array(claimSolutionSchema).optional(),
  /** 문제 타입 */
  type: claimReasonTypeSchema.optional(),
});
export type ClaimReasonItem = z.infer<typeof claimReasonItemSchema>;

// ============================================================================
// 교환/반품 상세 (MarketClaimResponse)
//
// result 페이지에서 사용하는 필드만 엄격히 정의하고, 깊은 미사용 중첩
// (refundInfo / order / answers)은 런타임 parse 실패 방지를 위해
// z.unknown()으로 느슨하게 처리한다. (order.ts model 컨벤션과 동일)
// ============================================================================

/**
 * 클레임 상세 응답 스키마 (MarketClaimResponse)
 */
// ============================================================================
// 교환/반품 상세 하위 정보 (MarketClaimResponse 내 RefundInfo/Answer/Order)
// ============================================================================

/** 환불 금액 배분 (PaymentBreakdownResponse 일부) */
export const claimRefundPaymentBreakdownSchema = z.object({
  /** 추가 배송비 */
  additionalDeliveryFee: z.number(),
  /** 배송비 */
  baseDeliveryFee: z.number(),
});

/** 환불 아이템 (PaymentRefundItemResponse 일부) */
export const claimRefundItemSchema = z.object({
  /** 환불 쿠폰 할인 합계 */
  refundCouponDiscountTotal: z.number(),
  /** 환불 포인트 합계 */
  refundPointPriceTotal: z.number(),
  /** 환불 상품 금액 합계 */
  refundPriceTotal: z.number(),
  /** 결제 금액 배분 */
  paymentBreakdown: claimRefundPaymentBreakdownSchema,
});

/** 환불 합계 (RefundSummaryResponse 일부) */
export const claimRefundSummarySchema = z.object({
  /** 실제 PG 환불 송금액 합계 */
  actualPgRefundAmount: z.number().optional(),
});

/** 환불 정보 (RefundInfoResponse) */
export const claimRefundInfoSchema = z.object({
  /** 환불 아이템 목록 */
  items: z.array(claimRefundItemSchema),
  /** 환불 합계 정보 */
  summary: claimRefundSummarySchema.optional(),
});
export type ClaimRefundInfo = z.infer<typeof claimRefundInfoSchema>;

/** 판매자 답변 (ClaimAnswerResponse) */
export const claimAnswerSchema = z.object({
  /** 답변 ID */
  id: z.number().optional(),
  /** 내용 */
  content: z.string().optional(),
  /** 생성 일자 */
  createdAt: z.string().optional(),
});

/** 주문 정보 (ClaimOrderResponse) */
export const claimOrderSchema = z.object({
  /** 주문 ID */
  id: z.number().optional(),
  /** 주문 코드 */
  code: z.string().optional(),
  /** 생성 일자 */
  createdAt: z.string().optional(),
});

export const claimDetailSchema = z.object({
  /** 판매자 답변 */
  answer: z.string().optional(),
  /** 답변 정보 */
  answers: z.array(claimAnswerSchema).optional(),
  /** 클레임 코드 */
  code: z.string().optional(),
  /** 추가 입력 사항 */
  content: z.string().optional(),
  /** 생성 일자 (ISO-8601, KST) */
  createdAt: z.string().optional(),
  /** 클레임 ID */
  id: z.number().optional(),
  /** 클레임 이미지 목록 */
  images: z.array(z.string()).optional(),
  /** 주문 정보 */
  order: claimOrderSchema.optional(),
  /** 결제 결과 정보 */
  paymentResult: paymentResultSchema.optional(),
  /** 처리 일자 (ISO-8601, KST) */
  processedAt: z.string().optional(),
  /** 상품 목록 (목록/상세 응답에 따라 일부 필드만 내려오므로 느슨하게 처리) */
  productOrders: z.array(productOrderSchema.partial()).optional(),
  /** 문제 사항 타입 */
  reason: claimReasonTypeSchema.optional(),
  /** 문제 사항 설명 */
  reasonDescription: z.string().optional(),
  /** 문제 사항 고객 안내 메시지 */
  reasonMessage: z.string().optional(),
  /** 결제 환불 정보 */
  refundInfo: claimRefundInfoSchema.optional(),
  /** 해결 방안 메시지 */
  solutionDescription: z.string().optional(),
  /** 해결방안 타입 */
  solutionType: claimSolutionTypeSchema.optional(),
  /** 클레임 상태 */
  status: claimDetailStatusSchema.optional(),
  /** 입점사 명 */
  vendorName: z.string().optional(),
});
export type ClaimDetail = z.infer<typeof claimDetailSchema>;

// ============================================================================
// 교환/반품 목록 페이지 (CursorPagedResponse, content = MarketClaimResponse[])
// ============================================================================

/** 교환/반품 목록 페이지 스키마 (무한 스크롤용 커서 페이징) */
export const claimHistoryPageSchema = z.object({
  /** 클레임 목록 */
  content: z.array(claimDetailSchema),
  /** 다음 페이지 여부 */
  hasNextPage: z.boolean(),
  /** 마지막 항목 ID (다음 페이지 요청 시 필요) */
  lastId: z.number().optional(),
  /** 조회된 응답 수 */
  size: z.number(),
});
export type ClaimHistoryPage = z.infer<typeof claimHistoryPageSchema>;

// ============================================================================
// 교환/반품 생성 요청 (MarketCreateClaimRequest)
// ============================================================================

/**
 * 클레임 생성 요청 스키마 (multipart의 'request' part)
 */
export const createClaimRequestSchema = z.object({
  /** 요청 사항 */
  content: z.string().optional(),
  /** 상품 주문 ID 리스트 */
  productOrderIds: z.array(z.number()),
  /** 문제 사항 */
  reason: claimReasonTypeSchema,
  /** 환불계좌 은행 */
  refundAccountBankCode: z.string().optional(),
  /** 환불계좌 예금주 */
  refundAccountName: z.string().optional(),
  /** 환불계좌 번호 */
  refundAccountNumber: z.string().optional(),
  /** 선택한 해결 사항 */
  solutionType: claimSolutionTypeSchema,
});
export type CreateClaimRequest = z.infer<typeof createClaimRequestSchema>;
